import React, { forwardRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Send } from 'lucide-react';
import { Button } from '../ui/button';
import { Textarea } from '../ui/textarea';

/**
 * Multi-line chat input with send button, keyboard shortcuts, and disclaimer.
 * Enter → send, Shift+Enter → newline.
 */
const ChatInput = forwardRef(function ChatInput(
  { value, onChange, onSubmit, disabled, placeholder, disclaimer, sendLabel },
  ref
) {
  const { t } = useTranslation();

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      if (!disabled && value.trim()) onSubmit();
    }
  };

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        if (!disabled && value.trim()) onSubmit();
      }}
      className="border-t border-slate-200 dark:border-slate-800 bg-white/95 dark:bg-[#0B0E14]/95 backdrop-blur px-3 sm:px-4 py-3"
    >
      <div className="flex items-end gap-2">
        <Textarea
          ref={ref}
          data-testid="ai-chat-input"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder={placeholder || t('aiAdvisor.inputPlaceholder')}
          rows={1}
          className="flex-1 resize-none min-h-[44px] max-h-32 rounded-2xl border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800/60 focus-visible:ring-2 focus-visible:ring-[#12355B] dark:focus-visible:ring-[#45C04A] focus-visible:ring-offset-0 transition-colors duration-200"
        />
        <Button
          data-testid="ai-chat-send-button"
          type="submit"
          disabled={disabled || !value.trim()}
          size="icon"
          aria-label={sendLabel || t('aiAdvisor.send')}
          className="rp-advisor-focus h-11 w-11 rounded-2xl bg-[#12355B] hover:bg-[#1565C0] dark:bg-[#45C04A] dark:hover:bg-[#65D95A] text-white dark:text-[#0F172A] disabled:opacity-40 disabled:cursor-not-allowed flex-shrink-0 hover:-translate-y-0.5 active:translate-y-0 transition-[transform,background-color] duration-200 ease-out"
        >
          <Send className="w-4 h-4" />
        </Button>
      </div>
      {disclaimer && (
        <p className="text-[10px] text-slate-400 dark:text-slate-500 mt-2 text-center">
          {disclaimer}
        </p>
      )}
    </form>
  );
});

export default ChatInput;
